const MONTH_LABELS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const MONTH_NAME_INDEX = MONTH_LABELS.reduce((acc, label, index) => {
  acc[label.toLowerCase()] = index;
  return acc;
}, {});

/** Calendar months oldest → newest for the trailing window (default 6). */
export function getTrailingMonthKeys(count = 6, now = new Date()) {
  const keys = [];
  for (let index = count - 1; index >= 0; index -= 1) {
    const target = new Date(now.getFullYear(), now.getMonth() - index, 1);
    keys.push({
      year: target.getFullYear(),
      month: MONTH_LABELS[target.getMonth()],
      monthIndex: target.getMonth(),
    });
  }
  return keys;
}

export function parseYearMonthName(name) {
  if (name == null) return null;
  const raw = String(name).trim();
  // "2026-Sep" or "2026-Sep-01"
  let match = raw.match(/^(\d{4})[-/ ]([A-Za-z]{3})/);
  if (match) {
    const monthIndex = MONTH_NAME_INDEX[match[2].toLowerCase()];
    if (monthIndex == null) return null;
    return { year: Number(match[1]), month: MONTH_LABELS[monthIndex], monthIndex };
  }
  // "Sep-26" / "Sep 2026"
  match = raw.match(/^([A-Za-z]{3})[-/ ](\d{2,4})$/);
  if (match) {
    const monthIndex = MONTH_NAME_INDEX[match[1].toLowerCase()];
    if (monthIndex == null) return null;
    let year = Number(match[2]);
    if (year < 100) year += 2000;
    return { year, month: MONTH_LABELS[monthIndex], monthIndex };
  }
  return null;
}

/**
 * Collapse merged/stale sixmonthApp rows into exactly `count` trailing months.
 * Chart title says six months — never plot a multi-year merged series.
 */
export function buildTrailingApplicationVolume(sixmonthApp, count = 6, now = new Date()) {
  const totals = new Map();
  (Array.isArray(sixmonthApp) ? sixmonthApp : []).forEach((row) => {
    const parsed = parseYearMonthName(row?.name);
    if (!parsed) return;
    const key = `${parsed.year}-${parsed.month}`;
    const valueKey = Object.keys(row).find((k) => k !== 'name') || 'Application';
    totals.set(key, (totals.get(key) || 0) + Number(row?.[valueKey] || 0));
  });

  return getTrailingMonthKeys(count, now).map(({ year, month }) => ({
    name: `${month}-${String(year).slice(-2)}`,
    Application: totals.get(`${year}-${month}`) || 0,
  }));
}

/**
 * Monthly Stage 05 (policies issued) for the trailing window.
 * Primary: appStageCountsByMonth (ignores claim-shaped rows without stage05).
 * Fallback: appChartData2.Approved for the current calendar year (stale admin caches).
 */
export function buildTrailingPoliciesIssued(
  appStageCountsByMonth,
  count = 6,
  now = new Date(),
  appChartData2,
) {
  const stage05ByMonth = new Map();
  (Array.isArray(appStageCountsByMonth) ? appStageCountsByMonth : []).forEach((item) => {
    if (item?.year == null || item?.month == null) return;
    if (!Object.prototype.hasOwnProperty.call(item, 'stage05')) return;
    const monthLabel = String(item.month).slice(0, 3);
    const monthIndex = MONTH_NAME_INDEX[monthLabel.toLowerCase()];
    if (monthIndex == null) return;
    const key = `${Number(item.year)}-${MONTH_LABELS[monthIndex]}`;
    stage05ByMonth.set(key, (stage05ByMonth.get(key) || 0) + Number(item.stage05 || 0));
  });

  const fromStageSeries = getTrailingMonthKeys(count, now).map(({ year, month }) => ({
    name: `${month}-${String(year).slice(-2)}`,
    Approved: stage05ByMonth.get(`${year}-${month}`) || 0,
  }));

  if (fromStageSeries.some((row) => Number(row.Approved) > 0)) {
    return fromStageSeries;
  }

  const approvedByMonth = new Map();
  const currentYear = now.getFullYear();
  (Array.isArray(appChartData2) ? appChartData2 : []).forEach((row) => {
    const monthLabel = String(row?.name || '').slice(0, 3);
    const monthIndex = MONTH_NAME_INDEX[monthLabel.toLowerCase()];
    if (monthIndex == null) return;
    approvedByMonth.set(`${currentYear}-${MONTH_LABELS[monthIndex]}`, Number(row.Approved || 0));
  });

  if (![...approvedByMonth.values()].some((value) => value > 0)) {
    return fromStageSeries;
  }

  return getTrailingMonthKeys(count, now).map(({ year, month }) => ({
    name: `${month}-${String(year).slice(-2)}`,
    Approved: approvedByMonth.get(`${year}-${month}`) || 0,
  }));
}
